import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore, api } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Loader2, Trash2, CheckCircle, Clock, User,
  Clipboard, Image as ImageIcon, X, Paperclip, LayoutGrid, List,
  ArrowRight, RotateCcw, Users, UserCheck, Pencil, Check
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';

const STATUSES = [
  { key: 'pending', label: 'Yeni', color: 'bg-amber-500', bgLight: 'bg-amber-50 border-amber-200', text: 'text-amber-700', icon: Clock },
  { key: 'in_progress', label: 'İşlemde', color: 'bg-blue-500', bgLight: 'bg-blue-50 border-blue-200', text: 'text-blue-700', icon: ArrowRight },
  { key: 'done', label: 'Tamamlandı', color: 'bg-emerald-500', bgLight: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', icon: CheckCircle },
];

// "Düzenlendi" rozeti için ufak relative-time helper. created_at ile
// updated_at farkı 2 sn'den büyükse içerik gerçekten edit'lenmiş demektir.
function wasEdited(todo) {
  if (!todo?.updated_at || !todo?.created_at) return false;
  const c = new Date(todo.created_at).getTime();
  const u = new Date(todo.updated_at).getTime();
  return Number.isFinite(c) && Number.isFinite(u) && (u - c) > 2000;
}
function formatRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMin = (Date.now() - d.getTime()) / 60000;
  if (diffMin < 1) return 'az önce';
  if (diffMin < 60) return `${Math.floor(diffMin)} dk önce`;
  if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)} sa önce`;
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const TodoList = () => {
  const { user } = useAuthStore();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newNote, setNewNote] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('todo_view') || 'board');
  const [superAdmins, setSuperAdmins] = useState([]);
  const [assignTo, setAssignTo] = useState('');
  const fileInputRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchTodos = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/todos');
      if (res.data?.success) setTodos(res.data.data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  const fetchSuperAdmins = useCallback(async () => {
    try {
      const res = await api.get('/admin/users');
      if (res.data?.success) {
        const admins = (res.data.data || []).filter(u => u.role === 'SUPER_ADMIN' && u.status === 'ACTIVE');
        setSuperAdmins(admins);
      }
    } catch {}
  }, []);

  useEffect(() => { fetchTodos(); fetchSuperAdmins(); }, [fetchTodos, fetchSuperAdmins]);

  const toggleView = (mode) => {
    setViewMode(mode);
    localStorage.setItem('todo_view', mode);
  };

  const handleAdd = async () => {
    if (!newTitle.trim()) { toast.error('Başlık giriniz'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', newTitle.trim());
      fd.append('note', newNote.trim());
      fd.append('assigned_to', assignTo || '');
      attachments.forEach(f => fd.append('files', f));
      await api.post('/todos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Yapılacak eklendi');
      setAddOpen(false);
      setNewTitle(''); setNewNote(''); setAttachments([]); setAssignTo('');
      fetchTodos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/todos/${id}`, { status });
      fetchTodos();
    } catch { toast.error('Güncelleme hatası'); }
  };

  const handleEdit = async (id, patch) => {
    // patch: { title?, note? } — boş başlık reddedilir backend'de
    try {
      const res = await api.patch(`/todos/${id}/content`, patch);
      toast.success('Güncellendi');
      // Backend updated_by/updated_at döndürür — local state'i tam kayıtla
      // güncelle ki "Düzenlendi" rozeti anında görünsün.
      const updated = res.data?.data;
      setTodos((prev) => prev.map((t) => {
        if (t.id !== id) return t;
        return updated ? { ...t, ...updated } : { ...t, ...patch };
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncelleme hatası');
      throw err;
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/todos/${id}`);
      toast.success('Silindi');
      fetchTodos();
    } catch { toast.error('Silme hatası'); }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) { setAttachments(prev => [...prev, file]); toast.success('Görsel yapıştırıldı'); }
      }
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  // Counts
  const counts = { pending: 0, in_progress: 0, done: 0 };
  todos.forEach(t => { if (counts[t.status] !== undefined) counts[t.status]++; });

  return (
    <div className="space-y-5" data-testid="todo-list-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Clipboard className="h-6 w-6 text-blue-500" />
            To Do List
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Yazılım istekleri ve yapılacaklar</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
            <button onClick={() => toggleView('board')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'board' ? 'bg-white dark:bg-slate-700 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`} data-testid="view-board" title="Board görünümü">
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button onClick={() => toggleView('list')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-slate-700 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`} data-testid="view-list" title="Liste görünümü">
              <List className="h-4 w-4" />
            </button>
          </div>
          <Button onClick={() => setAddOpen(true)} data-testid="add-todo-btn">
            <Plus className="h-4 w-4 mr-1" /> Yeni Ekle
          </Button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      ) : viewMode === 'board' ? (
        <BoardView todos={todos} counts={counts} onStatusChange={handleStatusChange} onDelete={handleDelete} onEdit={handleEdit} apiUrl={API_URL} />
      ) : (
        <ListView todos={todos} onStatusChange={handleStatusChange} onDelete={handleDelete} onEdit={handleEdit} apiUrl={API_URL} />
      )}

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="add-todo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-blue-500" /> Yeni Yapılacak Ekle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Başlık *</label>
              <Input placeholder="Kısa açıklama..." value={newTitle} onChange={e => setNewTitle(e.target.value)} data-testid="todo-title-input" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Detay / Not</label>
              <textarea
                className="w-full min-h-[100px] rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                placeholder="Detayları yazın veya ekran görüntüsü yapıştırın (Ctrl+V)..."
                value={newNote} onChange={e => setNewNote(e.target.value)} onPaste={handlePaste}
                data-testid="todo-note-input"
              />
            </div>
            {/* Assignment */}
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Atama</label>
              <Select value={assignTo || 'all'} onValueChange={v => setAssignTo(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9" data-testid="todo-assign-select">
                  <SelectValue placeholder="Tüm Super Adminler" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-blue-500" /> Tüm Super Adminler</span>
                  </SelectItem>
                  {superAdmins.map(sa => (
                    <SelectItem key={sa.id} value={sa.id}>
                      <span className="flex items-center gap-1.5"><UserCheck className="h-3.5 w-3.5 text-violet-500" /> {sa.full_name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div
              className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-4 text-center cursor-pointer hover:border-blue-300 transition-colors"
              onDragOver={e => e.preventDefault()} onDrop={handleFileDrop} onClick={() => fileInputRef.current?.click()}
            >
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={e => { setAttachments(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; }} />
              <ImageIcon className="h-6 w-6 mx-auto text-slate-300 mb-1" />
              <p className="text-xs text-slate-400">Dosya sürükle-bırak veya tıkla</p>
              <p className="text-[10px] text-slate-300">PrintScreen: Not alanında Ctrl+V</p>
            </div>
            {attachments.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {attachments.map((file, idx) => (
                  <div key={idx} className="relative group">
                    {file.type?.startsWith('image/') ? (
                      <img src={URL.createObjectURL(file)} alt="" className="h-14 w-14 object-cover rounded-lg border" />
                    ) : (
                      <div className="h-14 px-2 flex items-center rounded-lg border text-[10px] text-slate-500"><Paperclip className="h-3 w-3 mr-1" />{file.name?.slice(0, 12)}</div>
                    )}
                    <button className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => { e.stopPropagation(); removeAttachment(idx); }}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>İptal</Button>
            <Button onClick={handleAdd} disabled={saving} data-testid="save-todo-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ========== BOARD VIEW (Kanban with Drag & Drop) ==========
function BoardView({ todos, counts, onStatusChange, onDelete, onEdit, apiUrl }) {
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const handleDragStart = (e, todoId) => {
    setDragId(todoId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', todoId);
    // Make drag ghost slightly transparent
    if (e.target) e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    if (e.target) e.target.style.opacity = '1';
    setDragId(null);
    setDropTarget(null);
  };

  const handleDragOver = (e, statusKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(statusKey);
  };

  const handleDragLeave = (e, statusKey) => {
    // Only clear if leaving the column entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const { clientX, clientY } = e;
    if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
      setDropTarget(null);
    }
  };

  const handleDrop = (e, statusKey) => {
    e.preventDefault();
    setDropTarget(null);
    if (dragId) {
      const todo = todos.find(t => t.id === dragId);
      if (todo && todo.status !== statusKey) {
        onStatusChange(dragId, statusKey);
      }
    }
    setDragId(null);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {STATUSES.map(status => {
        const items = todos.filter(t => t.status === status.key);
        const isOver = dropTarget === status.key && dragId;
        return (
          <div key={status.key} className="flex flex-col">
            {/* Column header */}
            <div className={`flex items-center gap-2 px-3 py-2.5 rounded-t-xl border ${status.bgLight}`}>
              <div className={`w-2 h-2 rounded-full ${status.color}`} />
              <span className={`text-sm font-semibold ${status.text}`}>{status.label}</span>
              <Badge variant="secondary" className="text-[10px] ml-auto h-5">{counts[status.key]}</Badge>
            </div>

            {/* Column body - drop zone */}
            <div
              className={`flex-1 border border-t-0 rounded-b-xl p-2 space-y-2 min-h-[200px] transition-colors duration-200 ${isOver ? 'bg-blue-50/80 dark:bg-blue-900/20 border-blue-300 ring-2 ring-blue-200' : 'bg-slate-50/50 dark:bg-slate-900/30 border-slate-200 dark:border-slate-700'}`}
              onDragOver={(e) => handleDragOver(e, status.key)}
              onDragLeave={(e) => handleDragLeave(e, status.key)}
              onDrop={(e) => handleDrop(e, status.key)}
            >
              {items.length === 0 && (
                <p className={`text-center text-xs py-8 ${isOver ? 'text-blue-400' : 'text-slate-300'}`}>
                  {isOver ? 'Buraya bırak' : 'Kayıt yok'}
                </p>
              )}
              {items.map(todo => (
                <TodoCard
                  key={todo.id}
                  todo={todo}
                  status={status}
                  onStatusChange={onStatusChange}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  apiUrl={apiUrl}
                  isDragging={dragId === todo.id}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TodoCard({ todo, status, onStatusChange, onDelete, onEdit, apiUrl, isDragging, onDragStart, onDragEnd }) {
  const nextStatus = status.key === 'pending' ? 'in_progress' : status.key === 'in_progress' ? 'done' : null;
  const nextLabel = status.key === 'pending' ? 'İşleme Al' : status.key === 'in_progress' ? 'Tamamla' : null;

  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(todo.title || '');
  const [editNote, setEditNote] = useState(todo.note || '');
  const [savingEdit, setSavingEdit] = useState(false);

  const startEdit = (e) => {
    e?.stopPropagation();
    setEditTitle(todo.title || '');
    setEditNote(todo.note || '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditTitle(todo.title || '');
    setEditNote(todo.note || '');
  };

  const saveEdit = async () => {
    const t = editTitle.trim();
    if (!t) { toast.error('Başlık boş olamaz'); return; }
    const patch = {};
    if (t !== (todo.title || '')) patch.title = t;
    if (editNote !== (todo.note || '')) patch.note = editNote;
    if (Object.keys(patch).length === 0) { setEditing(false); return; }
    setSavingEdit(true);
    try {
      await onEdit(todo.id, patch);
      setEditing(false);
    } catch { /* toast already shown */ } finally { setSavingEdit(false); }
  };

  return (
    <div
      className={`bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3 shadow-sm hover:shadow-md transition-all group ${editing ? '' : 'cursor-grab active:cursor-grabbing'} ${isDragging ? 'opacity-40 scale-95 ring-2 ring-blue-300' : ''}`}
      draggable={!editing}
      onDragStart={(e) => !editing && onDragStart(e, todo.id)}
      onDragEnd={onDragEnd}
      data-testid={`todo-card-${todo.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        {editing ? (
          <Input
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            className="h-7 text-sm font-semibold"
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
              if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
            }}
            data-testid={`todo-edit-title-${todo.id}`}
          />
        ) : (
          <h4 className={`text-sm font-semibold text-slate-800 dark:text-slate-100 leading-tight ${status.key === 'done' ? 'line-through opacity-60' : ''}`}>
            {todo.title}
          </h4>
        )}
        {!editing && status.key !== 'done' && (
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            <button onClick={startEdit} className="p-0.5 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded" title="Düzenle" data-testid={`todo-edit-${todo.id}`}>
              <Pencil className="h-3 w-3 text-blue-400" />
            </button>
            <button onClick={() => onDelete(todo.id)} className="p-0.5 hover:bg-red-50 rounded" data-testid={`todo-delete-${todo.id}`}>
              <Trash2 className="h-3 w-3 text-red-400" />
            </button>
          </div>
        )}
        {!editing && status.key === 'done' && (
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            <button onClick={() => onDelete(todo.id)} className="p-0.5 hover:bg-red-50 rounded" data-testid={`todo-delete-${todo.id}`}>
              <Trash2 className="h-3 w-3 text-red-400" />
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <>
          <Textarea
            value={editNote}
            onChange={(e) => setEditNote(e.target.value)}
            className="text-xs min-h-[60px] mb-2"
            placeholder="Not (opsiyonel)"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); saveEdit(); }
              if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
            }}
            data-testid={`todo-edit-note-${todo.id}`}
          />
          <div className="flex items-center justify-end gap-1 mb-2">
            <button
              onClick={cancelEdit}
              disabled={savingEdit}
              className="text-[10px] px-2 py-1 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
              data-testid={`todo-edit-cancel-${todo.id}`}
            >
              İptal
            </button>
            <button
              onClick={saveEdit}
              disabled={savingEdit}
              className="text-[10px] px-2 py-1 rounded border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 flex items-center gap-1"
              data-testid={`todo-edit-save-${todo.id}`}
            >
              {savingEdit ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Check className="h-2.5 w-2.5" />}
              Kaydet
            </button>
          </div>
        </>
      ) : todo.note ? (
        <p className="text-xs text-slate-500 dark:text-slate-400 whitespace-pre-wrap mb-2 line-clamp-3">{todo.note}</p>
      ) : null}

      {/* Attachments preview */}
      {todo.attachments?.length > 0 && (
        <div className="flex gap-1.5 flex-wrap mb-2">
          {todo.attachments.map((att, idx) => (
            <a key={idx} href={`${apiUrl}/api/todos/${todo.id}/attachment/${idx}`} target="_blank" rel="noreferrer">
              {att.content_type?.startsWith('image/') ? (
                <img src={`${apiUrl}/api/todos/${todo.id}/attachment/${idx}`} alt={att.file_name} className="h-12 w-12 object-cover rounded border border-slate-200 hover:border-blue-400 transition-colors" />
              ) : (
                <div className="flex items-center gap-1 px-2 py-1 rounded border text-[10px] text-slate-500 hover:border-blue-400"><Paperclip className="h-2.5 w-2.5" />{att.file_name?.slice(0, 10)}</div>
              )}
            </a>
          ))}
        </div>
      )}

      {/* Meta & actions */}
      <div className="flex items-center justify-between pt-1.5 border-t border-slate-100 dark:border-slate-700">
        <div className="flex items-center gap-2 text-[10px] text-slate-400 flex-wrap">
          <span className="flex items-center gap-0.5"><User className="h-2.5 w-2.5" />{todo.created_by_name}</span>
          {todo.assigned_to_name && (
            <span className="flex items-center gap-0.5 text-violet-500 font-medium"><UserCheck className="h-2.5 w-2.5" />{todo.assigned_to_name}</span>
          )}
          {!todo.assigned_to && (
            <span className="flex items-center gap-0.5 text-blue-400"><Users className="h-2.5 w-2.5" />Herkes</span>
          )}
          <span>{new Date(todo.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}</span>
          {wasEdited(todo) && (
            <span
              className="flex items-center gap-0.5 text-amber-600 dark:text-amber-400 font-medium"
              title={`Düzenlendi: ${formatRelative(todo.updated_at)}${todo.updated_by_name ? ' · ' + todo.updated_by_name : ''}`}
              data-testid={`todo-edited-badge-${todo.id}`}
            >
              <Pencil className="h-2.5 w-2.5" />
              {formatRelative(todo.updated_at)}
            </span>
          )}
        </div>
        <div className="flex gap-1">
          {status.key === 'done' && (
            <button onClick={() => onStatusChange(todo.id, 'pending')} className="text-[10px] px-1.5 py-0.5 rounded border border-amber-200 text-amber-600 hover:bg-amber-50 flex items-center gap-0.5" data-testid={`todo-reopen-${todo.id}`}>
              <RotateCcw className="h-2.5 w-2.5" /> Aç
            </button>
          )}
          {nextStatus && (
            <button onClick={() => onStatusChange(todo.id, nextStatus)} className={`text-[10px] px-1.5 py-0.5 rounded border flex items-center gap-0.5 ${nextStatus === 'done' ? 'border-emerald-200 text-emerald-600 hover:bg-emerald-50' : 'border-blue-200 text-blue-600 hover:bg-blue-50'}`} data-testid={`todo-next-${todo.id}`}>
              {nextStatus === 'done' ? <CheckCircle className="h-2.5 w-2.5" /> : <ArrowRight className="h-2.5 w-2.5" />}
              {nextLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ========== LIST VIEW ==========
function ListView({ todos, onStatusChange, onDelete, onEdit, apiUrl }) {
  const statusInfo = (key) => STATUSES.find(s => s.key === key) || STATUSES[0];
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editNote, setEditNote] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const startEdit = (todo) => {
    setEditingId(todo.id);
    setEditTitle(todo.title || '');
    setEditNote(todo.note || '');
  };
  const cancelEdit = () => { setEditingId(null); setEditTitle(''); setEditNote(''); };
  const saveEdit = async (todo) => {
    const t = editTitle.trim();
    if (!t) { toast.error('Başlık boş olamaz'); return; }
    const patch = {};
    if (t !== (todo.title || '')) patch.title = t;
    if (editNote !== (todo.note || '')) patch.note = editNote;
    if (Object.keys(patch).length === 0) { cancelEdit(); return; }
    setSavingEdit(true);
    try {
      await onEdit(todo.id, patch);
      cancelEdit();
    } catch { /* toast already shown */ } finally { setSavingEdit(false); }
  };

  return (
    <div className="space-y-2">
      {todos.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-slate-400"><Clipboard className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>Henüz kayıt yok</p></CardContent></Card>
      ) : todos.map(todo => {
        const si = statusInfo(todo.status);
        const StatusIcon = si.icon;
        return (
          <Card key={todo.id} className={`transition-all ${todo.status === 'done' ? 'opacity-60' : ''}`} data-testid={`todo-row-${todo.id}`}>
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-3">
                {/* Status indicator */}
                <div className={`w-2 h-8 rounded-full shrink-0 ${si.color}`} />

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {editingId === todo.id ? (
                    <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                      <Input
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="h-7 text-sm font-semibold"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(todo); }
                          if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                        }}
                        data-testid={`todo-list-edit-title-${todo.id}`}
                      />
                      <Textarea
                        value={editNote}
                        onChange={(e) => setEditNote(e.target.value)}
                        className="text-xs min-h-[50px]"
                        placeholder="Not (opsiyonel)"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); saveEdit(todo); }
                          if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                        }}
                        data-testid={`todo-list-edit-note-${todo.id}`}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className={`font-semibold text-sm text-slate-800 dark:text-slate-100 ${todo.status === 'done' ? 'line-through' : ''}`}>{todo.title}</h3>
                        <Badge variant="outline" className={`text-[10px] border ${si.bgLight} ${si.text}`}>
                          <StatusIcon className="h-2.5 w-2.5 mr-0.5" />{si.label}
                        </Badge>
                        {todo.attachments?.length > 0 && (
                          <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Paperclip className="h-2.5 w-2.5" />{todo.attachments.length}</span>
                        )}
                      </div>
                      {todo.note && <p className="text-xs text-slate-500 truncate">{todo.note}</p>}
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400">
                        <span className="flex items-center gap-0.5"><User className="h-2.5 w-2.5" />{todo.created_by_name}</span>
                        {todo.assigned_to_name && (
                          <span className="flex items-center gap-0.5 text-violet-500 font-medium"><UserCheck className="h-2.5 w-2.5" />{todo.assigned_to_name}</span>
                        )}
                        {!todo.assigned_to && (
                          <span className="flex items-center gap-0.5 text-blue-400"><Users className="h-2.5 w-2.5" />Herkes</span>
                        )}
                        <span>{new Date(todo.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        {wasEdited(todo) && (
                          <span
                            className="flex items-center gap-0.5 text-amber-600 dark:text-amber-400 font-medium"
                            title={`Düzenlendi: ${formatRelative(todo.updated_at)}${todo.updated_by_name ? ' · ' + todo.updated_by_name : ''}`}
                          >
                            <Pencil className="h-2.5 w-2.5" />
                            Düzenlendi {formatRelative(todo.updated_at)}
                            {todo.updated_by_name && <span className="opacity-70"> · {todo.updated_by_name}</span>}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  {editingId === todo.id ? (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-slate-200" onClick={cancelEdit} disabled={savingEdit}>İptal</Button>
                      <Button
                        size="sm"
                        className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => saveEdit(todo)}
                        disabled={savingEdit}
                        data-testid={`todo-list-save-${todo.id}`}
                      >
                        {savingEdit ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Kaydet
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="icon" variant="ghost" className={`h-7 w-7 ${todo.status === 'done' ? 'hidden' : ''}`} onClick={() => startEdit(todo)} title="Düzenle" data-testid={`todo-list-edit-${todo.id}`}>
                        <Pencil className="h-3.5 w-3.5 text-blue-500" />
                      </Button>
                      {todo.status === 'pending' && (
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-blue-200 text-blue-600 hover:bg-blue-50" onClick={() => onStatusChange(todo.id, 'in_progress')}>
                          <ArrowRight className="h-3 w-3" /> İşleme Al
                        </Button>
                      )}
                      {todo.status !== 'done' && (
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-emerald-200 text-emerald-600 hover:bg-emerald-50" onClick={() => onStatusChange(todo.id, 'done')}>
                          <CheckCircle className="h-3 w-3" /> Tamamla
                        </Button>
                      )}
                      {todo.status === 'done' && (
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-amber-200 text-amber-600 hover:bg-amber-50" onClick={() => onStatusChange(todo.id, 'pending')}>
                          <RotateCcw className="h-3 w-3" /> Yeniden Aç
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onDelete(todo.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default TodoList;
